import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Chip, Panel } from '@promanage/ui';
import { useSession } from '../features/auth/session';
import { listCopies } from '../lib/offlineCopies';
import { useOnline } from '../lib/online';
import { Icon } from './Icon';

/**
 * With no connection, the way to what this phone can still open.
 *
 * The app opening in a dead zone is only half useful if the inspection is three screens away
 * through lists that cannot load. So the shell says plainly that there is no connection, and lists
 * the reports kept on this device — the one thing that DOES work — at the top of whatever page was
 * open. Gone the moment the browser says it is back online.
 *
 * Not on an inspection itself, which says the same thing about its own copy.
 */
export function OfflineBar() {
  const online = useOnline();
  const session = useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const onReport =
    pathname.startsWith('/properties/inspections/') && pathname !== '/properties/inspections/';
  const show = !online && session !== undefined && !onReport;

  const copies = useQuery({
    queryKey: ['offline-copy', 'list', session?.orgId, session?.user.id],
    queryFn: () =>
      session === undefined ? [] : listCopies({ userId: session.user.id, orgId: session.orgId }),
    enabled: show,
    // Read off the device, which answers with or without a network.
    networkMode: 'always',
    staleTime: 0,
  });

  if (!show) return null;

  const rows = copies.data ?? [];

  return (
    <Panel className="mb-4 flex flex-col gap-2 p-4" role="status">
      <span className="flex flex-wrap items-center gap-2">
        <Chip tone="warn">No connection</Chip>
        <span className="text-[0.8125rem] font-medium">
          {rows.length === 0
            ? 'Nothing is saved on this phone to open offline.'
            : 'Saved on this phone, and open without signal:'}
        </span>
      </span>

      {rows.length === 0 ? (
        <span className="text-[0.75rem] text-ink-muted">
          An inspection is kept here once it has been opened with a connection, while it is booked
          or in progress.
        </span>
      ) : (
        <ul className="-mx-2 flex flex-col">
          {rows.map((row) => (
            <li key={row.ref}>
              <Link
                to="/properties/inspections/$ref"
                params={{ ref: row.ref }}
                className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.875rem] font-medium">{row.title}</span>
                  <span className="block truncate text-[0.75rem] text-ink-muted">
                    {row.subtitle} · {row.ref}
                  </span>
                </span>
                <Icon name="chevron-right" size={14} className="shrink-0 text-ink-subtle" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
